import type { GameData } from '../data';
import { Vec3 } from './core/math';
import { Rng } from './core/rng';
import type { DamageSource, Pickup, PlayerControl, PlayerWeaponsState, Projectile, Ship, WeaponKind } from './entities';
import type { Quat } from './core/math';
import type { EnemyRole } from '../data';
import type { SimEvent } from './events';
import { Telemetry } from './telemetry/telemetry';
import type { CollisionWorld } from './world/collision';
import type { Fortress } from './encounter/fortress';
import type { ZoneData } from '../data';
import type { EquipmentEffects } from './loadout';

export interface DamageInfo {
  sourceShip: Ship | null;
  source: DamageSource;
  weapon: WeaponKind;
  /** Point d'application. */
  position: Vec3;
  /** Position d'origine du coup (tireur ou centre d'explosion). */
  origin: Vec3;
}

export type KillListener = (ship: Ship, info: DamageInfo) => void;

/** Demande d'apparition hors directeur de vagues (drones appelés par le boss). */
export interface SpawnQueueEntry {
  role: EnemyRole;
  position: Vec3;
  rotation: Quat;
}

/** Score arcade : points × multiplicateur de série. */
export interface ScoreState {
  total: number;
  multiplier: number;
  streakTimer: number;
  bestMultiplier: number;
}

/** État partagé par tous les sous-systèmes de la simulation. */
export class SimState {
  time = 0;
  tickCount = 0;
  readonly rng: Rng;
  readonly ships: Ship[] = [];
  readonly projectiles: Projectile[] = [];
  readonly pickups: Pickup[] = [];
  /** Télémétrie de la mission en cours (remplacée au début de chaque mission). */
  telemetry: Telemetry;
  player!: Ship;
  weapons!: PlayerWeaponsState;
  control!: PlayerControl;
  readonly score: ScoreState = { total: 0, multiplier: 1, streakTimer: 0, bestMultiplier: 1 };
  readonly spawnQueue: SpawnQueueEntry[] = [];
  /** Points d'amarrage (zones d'exclusion des ennemis). */
  docks: { keepOutDepth(p: Vec3): number; position: Vec3 }[] = [];
  /** Zone de la simulation et sa forteresse éventuelle (phase D). */
  zone = '';
  fortress: Fortress | null = null;
  /** Réglages de la zone (convoi, sol…). */
  zoneData: ZoneData | null = null;
  private readonly pending: SimEvent[] = [];
  private readonly killListeners: KillListener[] = [];
  private nextId = 1;

  /** Bouclier et utilitaire du joueur (phase C) ; vides sans chargement. */
  equipment: EquipmentEffects = { shield: null, emergencyRepair: null, jumpDrive: null };

  constructor(
    /** Réglages de la session (équipement appliqué) ; remplacés seulement par le panneau de réglage. */
    public data: GameData,
    readonly collision: CollisionWorld,
    readonly seed: number,
    readonly dt: number,
  ) {
    this.rng = new Rng(seed);
    this.telemetry = new Telemetry();
  }

  newId(): number {
    return this.nextId++;
  }

  emit(event: SimEvent): void {
    this.pending.push(event);
    this.telemetry.onEvent(event);
  }

  /** Retire et retourne les événements produits depuis le dernier appel. */
  drainEvents(): SimEvent[] {
    return this.pending.splice(0, this.pending.length);
  }

  onKill(listener: KillListener): void {
    this.killListeners.push(listener);
  }

  findShip(id: number | null): Ship | null {
    if (id === null) return null;
    return this.ships.find((s) => s.id === id) ?? null;
  }

  liveEnemies(): Ship[] {
    return this.ships.filter((s) => s.alive && s.team === 'enemy');
  }

  /**
   * Applique un dégât : le bouclier absorbe d'abord, le reste passe sur la coque.
   * Retourne la quantité réellement retirée (bouclier + coque, bornée par ce qui restait).
   */
  applyDamage(target: Ship, amount: number, info: DamageInfo): number {
    if (!target.alive || amount <= 0 || this.time < target.invulnerableUntil) return 0;
    let absorbed = 0;
    if (target.shield > 0 && target.shieldSpec) {
      absorbed = Math.min(amount, target.shield);
      target.shield -= absorbed;
      amount -= absorbed;
      if (target.shield <= 1e-9) {
        target.shield = 0;
        target.shieldDelay = target.shieldSpec.breakDelay;
        this.emit({ type: 'shieldBroken', time: this.time, shipId: target.id });
      } else {
        target.shieldDelay = target.shieldSpec.regenDelay;
      }
    } else if (target.shieldSpec && target.shield <= 0) {
      // Bouclier déjà rompu : chaque coup repousse sa remise en route.
      target.shieldDelay = Math.max(target.shieldDelay, target.shieldSpec.regenDelay);
    }
    const applied = Math.min(amount, target.health);
    target.health -= applied;
    target.lastDamageTime = this.time;
    const killed = target.health <= 0;
    const from = info.origin.sub(target.position).normalized(target.forward());
    this.emit({
      type: 'damage',
      time: this.time,
      targetId: target.id,
      targetKind: target.kind,
      sourceId: info.sourceShip?.id ?? null,
      source: info.source,
      weapon: info.weapon,
      applied,
      absorbed,
      killed,
      position: info.position.toArray(),
      fromDirection: from.toArray(),
    });
    if (killed) {
      target.health = 0;
      target.alive = false;
      this.emit({
        type: 'shipDestroyed',
        time: this.time,
        shipId: target.id,
        kind: target.kind,
        position: target.position.toArray(),
        radius: target.radius,
      });
      for (const listener of this.killListeners) listener(target, info);
    }
    return applied + absorbed;
  }

  /**
   * Dégâts de zone pleins dans le rayon, bloqués par les obstacles, tireur exclu
   * (UGameplayStatics::ApplyRadialDamage sans décroissance).
   */
  applyRadialDamage(center: Vec3, radius: number, damage: number, excludeId: number | null, info: Omit<DamageInfo, 'position' | 'origin'>): number {
    let events = 0;
    for (const ship of this.ships) {
      if (!ship.alive || ship.id === excludeId) continue;
      const toShip = ship.position.sub(center);
      const distance = toShip.length();
      if (distance - ship.radius > radius) continue;
      if (distance > ship.radius) {
        const hit = this.collision.raycast(center, toShip, distance - ship.radius);
        if (hit) continue;
      }
      const point = distance > 1e-6 ? center.add(toShip.scale(Math.max(0, distance - ship.radius) / distance)) : center.clone();
      if (this.applyDamage(ship, damage, { ...info, position: point, origin: center }) > 0) events++;
    }
    return events;
  }
}
